import { getAuthContextState } from '@/uptk/core/api/auth_context_store';
import { getTenantOverrideState } from '@/uptk/core/api/tenant_override_store';
import type { ModuleStudioRepository } from './ModuleStudioRepository';

const SHARED_READS = new Set<string>([
  'listPrototypeImports', 'listModuleSpecVersions', 'getModuleSpec', 'listGenerationRuns',
  'listPreviewDatasets', 'listTestPlans', 'listSimulations',
]);
const MAX_ENTRIES = 128;
const FRESH_MS = 30000;
// Les sondages d'analyses et de travaux restent des lectures reseau.
const hasActiveWork = (value: unknown): boolean => Array.isArray(value) && value.some(item =>
  item && typeof item === 'object' && 'status' in item && ['queued', 'running', 'pending', 'generating'].includes(String(item.status)));

export function cacheRepositoryReads(repository: ModuleStudioRepository): ModuleStudioRepository {
  const entries = new Map<string, { promise: Promise<unknown>; until: number }>();
  let scope = '';
  const clear = () => entries.clear();
  const wrapped = {} as ModuleStudioRepository;
  const target = wrapped as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>;
  const source = repository as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>;
  Object.keys(repository).forEach(method => {
    target[method] = (...args) => {
      const nextScope = JSON.stringify([getAuthContextState(), getTenantOverrideState()]);
      if (scope !== nextScope) { clear(); scope = nextScope; }
      if (!SHARED_READS.has(method)) {
        // Toute commande invalide avant ET apres sa resolution : aucune lecture pendant une ecriture ne reste figee.
        if (!/^(get|list|read|download|diff|estimate|preview|validate)/.test(method)) {
          clear();
          return Promise.resolve().then(() => source[method](...args)).finally(clear);
        }
        return source[method](...args);
      }
      const key = JSON.stringify([method, args]);
      const known = entries.get(key);
      if (known && known.until > Date.now()) return known.promise;
      const entry = { promise: Promise.resolve().then(() => source[method](...args)), until: Date.now() + FRESH_MS };
      entries.set(key, entry);
      if (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
      void entry.promise.then(value => {
        if (hasActiveWork(value) && entries.get(key) === entry) entries.delete(key);
      }, () => { if (entries.get(key) === entry) entries.delete(key); });
      return entry.promise;
    };
  });
  return wrapped;
}
